"use client";

import LoginContent from "./LoginContent";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useFormatPhone } from "@/hooks/useFormatPhone";
import { EmailLoginParamSchema, PhoneLoginParamSchema } from "@/types/auth";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLogin } from "@/hooks/auth/useAuth";

type FormFields = {
  email: string;
  countryCode: string;
  phone: string;
  password: string;
};

type LoginStepProps = {
  onSuccess: () => void;
  onRegisterClick: () => void;
};

const getLoginPayload = (data: FormFields, activeTab: "email" | "phone") => {
  if (activeTab === "phone") {
    return PhoneLoginParamSchema.parse({
      countryCode: "+855",
      phone: data.phone?.replace(/\D/g, ""),
      password: data.password,
    });
  }

  return EmailLoginParamSchema.parse({
    email: data.email?.trim(),
    password: data.password,
  });
};

const LoginStep = ({ onSuccess, onRegisterClick }: LoginStepProps) => {
  const { phone, email, setStep } = useAuthModal();
  const { formatPhone } = useFormatPhone();

  const methods = useForm<FormFields>({
    defaultValues: {
      email: "",
      countryCode: "+855",
      phone: phone ? formatPhone(phone, "KH") : "",
      password: "",
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const { mutate: login, isPending } = useLogin({
    onSuccess,
    onError: (message) => {
      setError("password", {
        type: "server",
        message,
      });
    },
  });

  const [tab, setTab] = useQueryState(
    "tab",
    parseAsStringEnum(["email", "phone"]).withDefault("email"),
  );

  const activeTab = tab === "phone" ? "phone" : "email";

  const handleTabChange = useCallback(
    (nextTab: "email" | "phone") => {
      clearErrors();
      void setTab(nextTab);
    },
    [clearErrors, setTab],
  );

  const handlePhoneChange = useCallback(
    (nextValue: string) => {
      setValue("phone", nextValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  useEffect(() => {
    if (!email) {
      return;
    }

    setValue("email", email, {
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [email, setValue]);

  const onSubmit = (data: FormFields) => {
    if (isPending) return;
    clearErrors();

    try {
      const payload = getLoginPayload(data, activeTab);
      login(payload);
    } catch {
      setError("password", {
        type: "validation",
        message:
          activeTab === "phone"
            ? "Enter a valid phone number and password."
            : "Enter a valid email and password.",
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <LoginContent
        activeTab={activeTab}
        phone={watch("phone")}
        isSubmitting={isPending}
        errorMessage={errors.password?.message || errors.root?.message}
        onTabChange={handleTabChange}
        onPhoneChange={handlePhoneChange}
        onSubmit={handleSubmit(onSubmit)}
        onRegisterClick={onRegisterClick}
        onForgotPasswordClick={() => void setStep("forgot-password")}
      />
    </FormProvider>
  );
};

export default LoginStep;
